import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { MessageCircle } from 'lucide-react';

import type {
  AdminAlertDto,
  AlertSeverityDto,
  CampaignHealthDto,
  OperationalViewFilters,
  ProfileMatrixRowDto,
} from '@/api/operational-view-client';
import { useCards, useHealth, useProfileMatrix } from '@/api/use-operational-view';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Kpi } from '@/components/ui/kpi';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { Textarea } from '@/components/ui/textarea';
import { HORS_JEUX_HINT, seniorityLabel } from '@/lib/operational-view-labels';
import {
  campaignStateLabel,
  cycleNumberLabel,
  cycleStateLabel,
  participationStateLabel,
} from '@/lib/campaign-labels';
import { establishmentStateLabel } from '@/lib/supplier-labels';
import { cn } from '@/lib/utils';
import { OperationalCardsPanel } from '@/routes/admin/operational-view/operational-view-cards';
import type {
  CampaignSupplierMonitoringDto,
  SupplierMonitoringRowDto,
  SupplierMonitoringSirenAggregateDto,
} from '@/api/supplier-monitoring-client';
import type { CampaignDetailDto } from '@/api/campaign-client';
import { useCampaignDetail } from '@/api/use-campaigns';
import { useSupplierMonitoring } from '@/api/use-supplier-monitoring';
import { useSendReminders } from '@/api/use-reminders';
import type { ReminderMode } from '@/api/reminders-client';

// =============================================================================
// campaign-monitoring-screen.tsx — Écran ADMIN « Suivi de campagne temps réel » (§2.5, concern A4).
// =============================================================================
// Câble le read-model de suivi DÉJÀ exposé par l'API (GET .../:campaignId/{health,cards,profile-matrix},
// AdminSessionGuard) à un écran de monitoring admin. AUCUNE règle métier ici : participation, Hors Jeux,
// alertes composite, taux de réponse par profil sont l'AUTORITÉ de l'API (calcul AU VOL, G0-S14-7a) ;
// l'UI LIT et habille.
//
// « Temps réel » = POLLING TanStack Query (`useHealth` refetchInterval) — PAS de WebSocket / infra realtime.
// Les KPI de santé sont enveloppés d'une live-region `aria-live="polite"` : chaque actualisation est
// annoncée aux lecteurs d'écran sans voler le focus.
//
// WCAG 2.1 AA (ST-NFR-03) :
//   - Santé = JUXTAPOSITION de KPI (<dl>/<dt>/<dd>), jamais un score unique (G0-S14-6).
//   - Sévérité d'alerte doublée d'un LIBELLÉ TEXTE (« Critique » / « Avertissement ») — jamais la
//     couleur seule ; le Badge n'est qu'un renfort visuel.
//   - Table de progression par profil : <caption> + <th scope>.
//   - États de chargement/erreur en role="status" / role="alert".
//
// SECTION FOURNISSEURS (V-SUIVI-FOURN-read, PFP-2026-07-21-002 — DBU §2.5 l.1161/l.1083/l.1165) : l'ancienne
// EXCLUSION (indicateurs de connexion / « connectés sans saisie », prétendue dépendante d'une migration M2
// `last_activity_at`) est CADUQUE sous DEC-20260721-8 / DEC-8 : la dernière activité est DÉRIVÉE READ-TIME
// côté API (aucune migration/backfill), et le badge « connecté sans saisie >48h » est un croisement EN MÉMOIRE
// last-activity × non-soumis calculé par le read-model analytics. L'UI LIT et habille (aucune règle métier).
// Bascule SIRET↔SIREN + agrégat « Complet » (tous les SIRET participants du SIREN ont soumis), repliable.

// Suivi campagne-level : aucun filtre profil/séniorité (référence stable → pas de refetch parasite).
const NO_FILTERS: OperationalViewFilters = {};

/** Sévérité API → libellé FR (porteur d'information ; la couleur n'est qu'un renfort). */
function severityLabel(severity: AlertSeverityDto): string {
  return severity === 'critical' ? 'Critique' : 'Avertissement';
}

export interface CampaignMonitoringScreenProps {
  readonly campaignId: string;
  /** Navigue vers les pages d'analyse « Vue opérationnelle » (porté par l'adaptateur routeur). */
  readonly onViewOperational?: () => void;
}

export function CampaignMonitoringScreen({
  campaignId,
  onViewOperational,
}: CampaignMonitoringScreenProps): React.JSX.Element {
  const healthQuery = useHealth(campaignId, NO_FILTERS);
  const cardsQuery = useCards(campaignId, NO_FILTERS);
  const matrixQuery = useProfileMatrix(campaignId, NO_FILTERS);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Suivi de campagne temps réel
          </h1>
          <p className="text-sm text-muted-foreground">
            Campagne <span className="font-mono">{campaignId}</span> — indicateurs
            de participation et alertes de pilotage, actualisés en continu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onViewOperational ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onViewOperational}
            >
              Vue opérationnelle
            </Button>
          ) : null}
        </div>
      </header>

      <PilotageHeaderPanel campaignId={campaignId} />

      <HealthPanel
        isPending={healthQuery.isPending}
        isError={healthQuery.isError}
        errorMessage={healthQuery.error?.message}
        health={healthQuery.data}
      />

      <AlertsPanel
        isPending={healthQuery.isPending}
        isError={healthQuery.isError}
        alerts={healthQuery.data?.alerts}
      />

      <OperationalCardsPanel
        isPending={cardsQuery.isPending}
        isError={cardsQuery.isError}
        errorMessage={cardsQuery.error?.message}
        cards={cardsQuery.data}
      />

      <ProfileProgressPanel
        isPending={matrixQuery.isPending}
        isError={matrixQuery.isError}
        errorMessage={matrixQuery.error?.message}
        rows={matrixQuery.data?.rows}
      />

      <SupplierMonitoringPanel campaignId={campaignId} />
    </div>
  );
}

// =============================================================================
// En-tête de pilotage (DBU §2.5 l.1133) : nom de l'opération, clients rattachés, état global,
// échéances critiques.
// =============================================================================
// COMPOSITION UI (aucun nouveau read-model, aucune requête cross-schéma) : le suivi temps réel LIT
// le détail de campagne DÉJÀ exposé par le BC campaign (GET /admin/campaigns/:id, AdminSessionGuard)
// via le MÊME port ACL (`useCampaignDetail`). L'écran juxtapose deux read-models (suivi analytique +
// détail campagne) au niveau de la SPA — l'API reste l'autorité (état, périmètre, échéances). L'UI LIT
// et habille : aucune règle métier ici.
//
// WCAG 2.1 AA (ST-NFR-03) : en-tête en <dl>/<dt>/<dd> ; état global = LIBELLÉ TEXTE (campaignStateLabel)
// doublé d'un Badge (renfort couleur, jamais seul) ; états de chargement/erreur en role="status"/"alert".

/**
 * En-tête de pilotage : compose le détail de campagne (intitulé, périmètre client, état, planning des
 * cycles) au-dessus du suivi temps réel. Lecture pure via le port campaign — aucun modèle nouveau.
 */
function PilotageHeaderPanel({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const query = useCampaignDetail(campaignId);
  return (
    <Card>
      <CardHeader>
        <CardTitle>En-tête de pilotage</CardTitle>
        <CardDescription>
          Opération, clients rattachés, état global et échéances critiques.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement de l’en-tête de pilotage…
          </p>
        ) : query.isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {query.error.message}
          </p>
        ) : query.data === undefined ? null : (
          <PilotageHeaderContent campaign={query.data} />
        )}
      </CardContent>
    </Card>
  );
}

/** Corps de l'en-tête de pilotage : projection des faits du détail campagne (aucune règle métier). */
function PilotageHeaderContent({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const state = campaignStateLabel(campaign.state);
  const hasWindow = campaign.startDate !== null || campaign.endDate !== null;
  const cycles = [...campaign.cycles].sort((a, b) => a.cycleNumber - b.cycleNumber);
  const hasDeadlines = hasWindow || cycles.length > 0;
  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-1">
        <dt className="text-xs font-medium text-muted-foreground">
          Nom de l’opération
        </dt>
        <dd className="text-base font-semibold">{campaign.intitule}</dd>
      </div>
      <div className="space-y-1">
        <dt className="text-xs font-medium text-muted-foreground">État global</dt>
        <dd>
          <Badge variant={state.variant}>{state.label}</Badge>
        </dd>
      </div>
      <div className="space-y-1">
        <dt className="text-xs font-medium text-muted-foreground">
          Clients rattachés
        </dt>
        <dd>
          {campaign.clientScopes.length === 0 ? (
            <span className="text-muted-foreground">Aucun client rattaché</span>
          ) : (
            <ul className="space-y-0.5">
              {campaign.clientScopes.map((scope) => (
                <li key={scope.scopeId} className="font-mono text-sm">
                  {scope.clientSiren}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </div>
      <div className="space-y-1">
        <dt className="text-xs font-medium text-muted-foreground">
          Échéances critiques
        </dt>
        <dd>
          {!hasDeadlines ? (
            <span className="text-muted-foreground">
              Aucune échéance planifiée
            </span>
          ) : (
            <ul className="space-y-0.5 text-sm">
              {hasWindow ? (
                <li>
                  Période&nbsp;: du{' '}
                  <span className="tabular-nums">
                    {shortDate(campaign.startDate)}
                  </span>{' '}
                  au{' '}
                  <span className="tabular-nums">
                    {shortDate(campaign.endDate)}
                  </span>
                </li>
              ) : null}
              {cycles.map((cycle) => (
                <li key={cycle.cycleId}>
                  {cycleNumberLabel(cycle.cycleNumber)} — clôture le{' '}
                  <span className="tabular-nums">{shortDate(cycle.closeAt)}</span>{' '}
                  <span className="text-muted-foreground">
                    ({cycleStateLabel(cycle.state).label})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </dd>
      </div>
    </dl>
  );
}

// =============================================================================
// Section Fournisseurs (V-SUIVI-FOURN-read). Table analytique + bascule SIRET↔SIREN + agrégat « Complet ».
// =============================================================================

/** État CGU / dernière activité : un octet ISO → date FR courte déterministe (YYYY-MM-DD → JJ/MM/AAAA). */
function shortDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const [date] = iso.split('T');
  const parts = (date ?? '').split('-');
  if (parts.length !== 3) {
    return iso;
  }
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/** Taux de complétion [0,1] → « NN % » ; null (périmètre vide) → « — ». */
function completionLabel(pct: number | null): string {
  return pct === null ? '—' : `${String(Math.round(pct * 100))} %`;
}

/** Statut de référencement (etablissementState) → libellé FR ; null (établissement introuvable) → « — ». */
function etablissementStateLabel(
  state: SupplierMonitoringRowDto['etablissementState'],
): string {
  return state === null ? '—' : establishmentStateLabel(state).label;
}

type MonitoringView = 'siret' | 'siren';

// =============================================================================
// R15-COM-05 — Modale de COMPOSITION de relance (objet + message éditables + aperçu CCI + confirmation).
// =============================================================================

/** Objet PRÉ-RÉDIGÉ par défaut de la relance (modifiable avant envoi). */
const DEFAULT_RELANCE_SUBJECT =
  'Rappel : votre soumission de référencement JEKO est attendue';

/**
 * Message PRÉ-RÉDIGÉ par défaut de la relance (modifiable avant envoi). Contenu ÉDITORIAL COMMUN, non
 * nominatif. ANTI-LLMISM (invariant n°6) : pas d'em-dash en substitut parenthétique.
 */
const DEFAULT_RELANCE_BODY = [
  'Bonjour,',
  '',
  'Nous vous rappelons que votre soumission dans le cadre de la campagne de',
  'référencement JEKO est attendue. Merci de finaliser votre saisie avant',
  "l'échéance du cycle en cours.",
  '',
  'Nous restons à votre disposition pour toute question.',
  '',
  "L'équipe JEKO Référencement",
].join('\n');

/**
 * Demande de relance EN ATTENTE de confirmation (modale ouverte). Porte la cible (mode + sélection) et
 * l'APERÇU des destinataires en copie cachée (CCI). Les envois sont individualisés côté worker (un email
 * séparé par SIRET) : les destinataires ne sont jamais mutuellement visibles.
 */
export interface RelanceRequest {
  readonly mode: ReminderMode;
  /** Mode `individual` : SIRET ciblés. */
  readonly sirets?: readonly string[];
  /** Mode `siren` : SIREN ciblé. */
  readonly siren?: string;
  /** SIRET affichés dans l'aperçu CCI (destinataires prévisionnels). */
  readonly recipients: readonly string[];
  /** Titre de la modale (contexte de la relance). */
  readonly title: string;
  /** Description de la modale (portée de la relance). */
  readonly context: string;
}

/** Aperçu des destinataires en copie cachée (CCI) — DBU §2.7.1 l.1506. */
function CciRecipientsPreview({
  recipients,
}: {
  readonly recipients: readonly string[];
}): React.JSX.Element {
  return (
    <section
      aria-label="Destinataires en copie cachée"
      className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
    >
      <p className="text-sm font-medium">
        Destinataires en copie cachée (CCI) : {String(recipients.length)}
      </p>
      <p className="text-xs text-muted-foreground">
        Chaque établissement reçoit un email séparé. Les destinataires ne sont
        pas visibles les uns des autres. Les dossiers déjà soumis sont ignorés à
        l’envoi.
      </p>
      {recipients.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucun destinataire à relancer dans cette sélection.
        </p>
      ) : (
        <ul className="max-h-32 space-y-0.5 overflow-y-auto">
          {recipients.map((siret) => (
            <li key={siret} className="font-mono text-xs">
              {siret}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Modale de composition : objet + message ÉDITABLES, aperçu CCI et CONFIRMATION obligatoire (DBU §2.7.1
 * l.1506/1508). L'envoi n'a lieu qu'à la confirmation ; la modale est fermable sans envoyer. Piège de focus
 * complet porté par le composant DS `Modal` (WCAG 2.1.2 / 2.4.3).
 */
export function RelanceCompositionModal({
  request,
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  isPending,
  onConfirm,
  onCancel,
}: {
  readonly request: RelanceRequest | null;
  readonly subject: string;
  readonly body: string;
  readonly onSubjectChange: (value: string) => void;
  readonly onBodyChange: (value: string) => void;
  readonly isPending: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const canConfirm =
    subject.trim().length > 0 && body.trim().length > 0 && !isPending;
  return (
    <Modal
      open={request !== null}
      onClose={onCancel}
      title={request?.title ?? ''}
      {...(request !== null ? { description: request.context } : {})}
    >
      {request !== null ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) {
              onConfirm();
            }
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="relance-subject">Objet de l’email</Label>
            <Input
              id="relance-subject"
              name="subject"
              value={subject}
              maxLength={200}
              onChange={(e) => {
                onSubjectChange(e.target.value);
              }}
              aria-invalid={subject.trim().length === 0}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="relance-body">Message</Label>
            <Textarea
              id="relance-body"
              name="body"
              value={body}
              maxLength={5000}
              onChange={(e) => {
                onBodyChange(e.target.value);
              }}
              aria-invalid={body.trim().length === 0}
            />
          </div>
          <CciRecipientsPreview recipients={request.recipients} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
            <Button type="submit" disabled={!canConfirm}>
              {isPending ? 'Envoi…' : 'Confirmer l’envoi'}
            </Button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
}

/**
 * R15-COM-05 / A-16 — Orchestration de la relance manuelle admin (individuelle / sélection / groupée / SIREN),
 * PARTAGÉE entre le suivi temps réel (`SupplierMonitoringPanel`) et la section « Fournisseurs » du registre des
 * campagnes (A-12). Chaque déclencheur OUVRE la modale de composition (`RelanceCompositionModal`) ; l'envoi
 * (`useSendReminders`) n'a lieu qu'à la confirmation. Aucune règle métier : le worker filtre les non-soumis.
 */
export function useRelanceComposer(
  campaignId: string,
  monitoring: CampaignSupplierMonitoringDto | undefined,
) {
  // A-16 / V-SUIVI-FOURN-relance : relance MANUELLE admin (individuelle / groupée / SIREN). L'API relaie une
  // demande INFRA au worker (qui FILTRE les non-soumis) ; l'UI DÉCLENCHE et habille (aucune règle métier).
  const relance = useSendReminders(campaignId);

  // R15-COM-05 : la relance n'est plus émise au clic. Chaque déclencheur OUVRE une modale de COMPOSITION
  // (objet + message éditables) avec un APERÇU des destinataires en copie cachée (CCI) et une CONFIRMATION
  // explicite. L'envoi effectif (relance.mutate, avec subject/body) n'a lieu qu'à la confirmation ; la
  // modale est fermable sans envoyer. Le worker relaie en CCI (un email séparé par SIRET, anti-fuite).
  const [pending, setPending] = React.useState<RelanceRequest | null>(null);
  const [subject, setSubject] = React.useState<string>('');
  const [body, setBody] = React.useState<string>('');

  // VRAIE condition d'activation de la relance (PAS le cycleNumber échoté d'un filtre) :
  //   • campagne OUVERTE = un cycle courant est ouvert → `openCycleNumber` exposé par le read-model suivi ;
  //   • présence d'au moins un fournisseur NON soumis à relancer.
  // `openCycleNumber` est aussi la CIBLE (échéance) transmise au worker.
  const rows = React.useMemo<readonly SupplierMonitoringRowDto[]>(
    () => monitoring?.rows ?? [],
    [monitoring],
  );
  const openCycle = monitoring?.openCycleNumber ?? null;
  const hasUnsubmitted = rows.some((r) => !r.submitted);
  const canRelance = openCycle !== null && hasUnsubmitted && !relance.isPending;

  // Ouvre la modale de composition avec les valeurs PRÉ-RÉDIGÉES par défaut (objet + message modifiables).
  const openRelance = React.useCallback((request: RelanceRequest): void => {
    setSubject(DEFAULT_RELANCE_SUBJECT);
    setBody(DEFAULT_RELANCE_BODY);
    setPending(request);
  }, []);

  const relanceGroup = React.useCallback((): void => {
    if (openCycle === null) return;
    const recipients = rows
      .filter((r) => !r.submitted)
      .map((r) => r.supplierSiret);
    openRelance({
      mode: 'group',
      recipients,
      title: 'Relancer les fournisseurs non soumis',
      context:
        'Tous les fournisseurs non soumis de la campagne recevront la relance ; les dossiers déjà soumis sont ignorés.',
    });
  }, [openCycle, rows, openRelance]);

  const relanceSiret = React.useCallback(
    (siret: string): void => {
      if (openCycle === null) return;
      openRelance({
        mode: 'individual',
        sirets: [siret],
        recipients: [siret],
        title: 'Relancer un fournisseur',
        context: 'Ce fournisseur recevra une relance individuelle par email.',
      });
    },
    [openCycle, openRelance],
  );

  const relanceSelection = React.useCallback(
    (sirets: readonly string[]): void => {
      if (openCycle === null || sirets.length === 0) return;
      openRelance({
        mode: 'individual',
        sirets,
        recipients: sirets,
        title: 'Relancer la sélection',
        context:
          'Les fournisseurs sélectionnés recevront chacun une relance par email (envois séparés).',
      });
    },
    [openCycle, openRelance],
  );

  const relanceSiren = React.useCallback(
    (siren: string): void => {
      if (openCycle === null) return;
      const recipients = rows
        .filter((r) => r.siren === siren && !r.submitted)
        .map((r) => r.supplierSiret);
      openRelance({
        mode: 'siren',
        siren,
        recipients,
        title: 'Relancer les établissements d’un SIREN',
        context:
          'Les établissements non soumis de ce SIREN recevront une relance par email.',
      });
    },
    [openCycle, rows, openRelance],
  );

  const closeRelance = React.useCallback((): void => {
    setPending(null);
  }, []);

  const confirmRelance = React.useCallback((): void => {
    if (pending === null || openCycle === null) return;
    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();
    if (trimmedSubject.length === 0 || trimmedBody.length === 0) return;
    if (pending.mode === 'individual') {
      relance.mutate({
        campaignId,
        mode: 'individual',
        cycle: openCycle,
        sirets: pending.sirets ?? [],
        subject: trimmedSubject,
        body: trimmedBody,
      });
    } else if (pending.mode === 'siren') {
      if (pending.siren === undefined) return;
      relance.mutate({
        campaignId,
        mode: 'siren',
        cycle: openCycle,
        siren: pending.siren,
        subject: trimmedSubject,
        body: trimmedBody,
      });
    } else {
      relance.mutate({
        campaignId,
        mode: 'group',
        cycle: openCycle,
        subject: trimmedSubject,
        body: trimmedBody,
      });
    }
    setPending(null);
  }, [pending, openCycle, subject, body, relance, campaignId]);

  return {
    relance,
    pending,
    subject,
    body,
    setSubject,
    setBody,
    openCycle,
    hasUnsubmitted,
    canRelance,
    relanceGroup,
    relanceSiret,
    relanceSelection,
    relanceSiren,
    closeRelance,
    confirmRelance,
  };
}

/**
 * Section « Suivi fournisseurs » : contact référent, connexion, état CGU, statut réf., taux de complétion,
 * historique. Bascule SIRET↔SIREN (repliable). Tout est calculé AU VOL côté API (composition EN MÉMOIRE via
 * ports ACL, jamais de JOIN cross-schéma) ; l'UI LIT et habille. WCAG : <caption> + <th scope>, badge en
 * LIBELLÉ TEXTE (jamais couleur seule), toggle en radiogroup accessible.
 */
function SupplierMonitoringPanel({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const query = useSupplierMonitoring(campaignId);
  const [view, setView] = React.useState<MonitoringView>('siret');
  const {
    relance,
    pending,
    subject,
    body,
    setSubject,
    setBody,
    openCycle,
    hasUnsubmitted,
    canRelance,
    relanceGroup,
    relanceSiret,
    relanceSelection,
    relanceSiren,
    closeRelance,
    confirmRelance,
  } = useRelanceComposer(campaignId, query.data);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle>Suivi fournisseurs</CardTitle>
            <CardDescription>
              Contact référent, dernière connexion, état CGU, statut de référencement, taux de
              complétion et historique — composés au vol côté serveur.
            </CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={relanceGroup}
              disabled={!canRelance}
              title={
                openCycle === null
                  ? 'Aucun cycle courant ouvert à relancer'
                  : !hasUnsubmitted
                    ? 'Tous les fournisseurs du périmètre ont soumis'
                    : 'Relancer par email tous les fournisseurs non soumis de la campagne'
              }
            >
              Relancer les non-soumis
            </Button>
            <div
              role="radiogroup"
              aria-label="Granularité d’affichage"
              className="flex items-center gap-1 rounded-md border border-border p-1"
            >
              <Button
                type="button"
                size="sm"
                role="radio"
                aria-checked={view === 'siret'}
                variant={view === 'siret' ? 'default' : 'ghost'}
                onClick={() => {
                  setView('siret');
                }}
              >
                Par SIRET
              </Button>
              <Button
                type="button"
                size="sm"
                role="radio"
                aria-checked={view === 'siren'}
                variant={view === 'siren' ? 'default' : 'ghost'}
                onClick={() => {
                  setView('siren');
                }}
              >
                Par SIREN
              </Button>
            </div>
          </div>
        </div>
        {/* Feedback de relance (WCAG : région annoncée, jamais couleur seule). */}
        <div aria-live="polite" className="min-h-[1.25rem] text-sm">
          {relance.isPending ? (
            <span role="status" className="text-muted-foreground">
              Relance en cours…
            </span>
          ) : relance.isError ? (
            <span role="alert" className="text-destructive">
              Échec de la relance : {relance.error.message}
            </span>
          ) : relance.isSuccess ? (
            <span role="status" className="text-muted-foreground">
              Demande de relance transmise ({String(relance.data.targetedSiretCount)} fournisseur(s)
              ciblé(s) ; les dossiers déjà soumis sont ignorés).
            </span>
          ) : openCycle === null ? (
            <span className="text-muted-foreground">
              Relance indisponible : aucun cycle courant ouvert.
            </span>
          ) : !hasUnsubmitted ? (
            <span className="text-muted-foreground">
              Tous les fournisseurs du périmètre ont soumis : aucune relance nécessaire.
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement du suivi fournisseurs…
          </p>
        ) : query.isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {query.error.message}
          </p>
        ) : query.data === undefined || query.data.rows.length === 0 ? (
          <p role="status" className="text-muted-foreground">
            Aucun fournisseur participant dans le périmètre.
          </p>
        ) : view === 'siret' ? (
          <SupplierMonitoringSiretTable
            campaignId={campaignId}
            rows={query.data.rows}
            canRelance={canRelance}
            onRelanceSiret={relanceSiret}
            onRelanceSelection={relanceSelection}
          />
        ) : (
          <SupplierMonitoringSirenTable
            aggregates={query.data.sirenAggregates}
            rows={query.data.rows}
            canRelance={canRelance}
            onRelanceSiren={relanceSiren}
          />
        )}
      </CardContent>
      <RelanceCompositionModal
        request={pending}
        subject={subject}
        body={body}
        onSubjectChange={setSubject}
        onBodyChange={setBody}
        isPending={relance.isPending}
        onConfirm={confirmRelance}
        onCancel={closeRelance}
      />
    </Card>
  );
}

/**
 * Vue SIRET : une ligne par fournisseur participant. Colonnes DBU l.1137 : identité + contact référent,
 * indicateurs de connexion (dernière activité + alerte « connecté sans saisie >48h »), état réglementaire et
 * opérationnel (CGU + statut de référencement `etablissementState`), performance (complétion + historique de
 * participation). Tous les statuts en LIBELLÉ TEXTE (WCAG : jamais couleur seule).
 */
function SupplierMonitoringSiretTable({
  campaignId,
  rows,
  canRelance,
  onRelanceSiret,
  onRelanceSelection,
}: {
  /** Campagne courante — cible du deep-link « Ouvrir le chat » (R15-COM-09 (c)). */
  readonly campaignId: string;
  readonly rows: readonly SupplierMonitoringRowDto[];
  /** Relance disponible (cycle courant connu + pas de relance en cours). */
  readonly canRelance: boolean;
  /** Déclenche une relance INDIVIDUELLE (mode individual, un seul SIRET). */
  readonly onRelanceSiret: (siret: string) => void;
  /** R15-COM-05 : relance d'un SOUS-ENSEMBLE arbitraire de SIRET cochés (mode individual, CCI). */
  readonly onRelanceSelection: (sirets: readonly string[]) => void;
}): React.JSX.Element {
  // Sélection multiple LOCALE à la table (état de vue éphémère, jamais persisté — RULE_REF_006).
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const toggle = React.useCallback((siret: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siret)) {
        next.delete(siret);
      } else {
        next.add(siret);
      }
      return next;
    });
  }, []);

  const onRelanceSelectionClick = React.useCallback((): void => {
    onRelanceSelection([...selected]);
  }, [onRelanceSelection, selected]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRelanceSelectionClick}
          disabled={!canRelance || selected.size === 0}
        >
          {`Relancer la sélection (${String(selected.size)})`}
        </Button>
      </div>
      <div className="overflow-x-auto">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Suivi des fournisseurs participants (sélection, contact référent, connexion, CGU, statut de
          référencement, complétion, historique de participation, relance individuelle)
        </caption>
        <thead>
          <tr className="border-b border-border text-left">
            <th scope="col" className="py-2 pr-4 font-medium">
              <span className="sr-only">Sélection</span>
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Fournisseur
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Contact référent
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Dernière connexion
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              CGU
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Statut référencement
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Complétion
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Historique de participation
            </th>
            <th scope="col" className="py-2 font-medium">
              Relance
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.supplierSiret}
              className="border-b border-border align-top last:border-0"
            >
              <td className="py-2 pr-4">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(row.supplierSiret)}
                  onChange={() => {
                    toggle(row.supplierSiret);
                  }}
                  aria-label={`Sélectionner le fournisseur ${row.raisonSociale ?? row.supplierSiret}`}
                />
              </td>
              <th scope="row" className="py-2 pr-4 font-normal">
                <span className="block font-medium">{row.raisonSociale ?? '—'}</span>
                <span className="block font-mono text-xs text-muted-foreground">
                  {row.supplierSiret}
                </span>
              </th>
              <td className="py-2 pr-4">
                {row.referent === null ? (
                  <span className="text-muted-foreground">Référent inconnu</span>
                ) : (
                  <span className="block">
                    <span className="block">{row.referent.nomComplet ?? '—'}</span>
                    <span className="block text-xs text-muted-foreground">
                      {row.referent.email ?? '—'}
                      {row.referent.telephone !== null ? ` · ${row.referent.telephone}` : ''}
                    </span>
                  </span>
                )}
              </td>
              <td className="py-2 pr-4">
                {row.lastActivityAt === null ? (
                  <span className="text-muted-foreground">Jamais connecté</span>
                ) : (
                  <span className="tabular-nums">{shortDate(row.lastActivityAt)}</span>
                )}
                {row.connectedNoSubmissionOver48h ? (
                  <Badge variant="secondary" className="mt-1 block w-fit">
                    Connecté sans saisie &gt; 48h
                  </Badge>
                ) : null}
              </td>
              <td className="py-2 pr-4">
                {row.cguSigned ? 'Signée' : 'Non signée'}
              </td>
              <td className="py-2 pr-4">{etablissementStateLabel(row.etablissementState)}</td>
              <td className="py-2 pr-4 tabular-nums">
                {completionLabel(row.completionPct)}
                <span className="block text-xs text-muted-foreground">
                  {String(row.respondedCoupleCount)} / {String(row.perimeterCoupleCount)}
                </span>
              </td>
              <td className="py-2 pr-4">
                <ParticipationHistoryCell row={row} />
              </td>
              <td className="py-2">
                <div className="flex flex-col items-start gap-1">
                  {row.submitted ? (
                    <span className="text-xs text-muted-foreground">Soumis</span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!canRelance}
                      onClick={() => {
                        onRelanceSiret(row.supplierSiret);
                      }}
                      aria-label={`Relancer le fournisseur ${row.raisonSociale ?? row.supplierSiret}`}
                    >
                      Relancer
                    </Button>
                  )}
                  {/* R15-COM-09 (c) — « Ouvrir le chat » : deep-link vers la surface chat COM-02
                      ciblant le fil (campagne, fournisseur). Aucun endpoint net-new : réutilise la
                      route /admin/conversations (search `campaign`). La RLS reste la frontière au
                      chargement du fil (admin-read). <Link> = ancre réelle (SPA, pas de reload). */}
                  <Link
                    to="/admin/conversations"
                    search={{ campaign: campaignId }}
                    className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
                    aria-label={`Ouvrir le chat avec le fournisseur ${row.raisonSociale ?? row.supplierSiret}`}
                  >
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    Ouvrir le chat
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/**
 * Historique de participation (DBU l.1137, OWNED campaign) : état de participation en LIBELLÉ TEXTE +
 * jalons datés (invité / soumis / dernière relance). Aucune règle métier — projection des faits du DTO.
 */
function ParticipationHistoryCell({
  row,
}: {
  readonly row: SupplierMonitoringRowDto;
}): React.JSX.Element {
  return (
    <span className="block">
      <span className="block font-medium">
        {participationStateLabel(row.participationState).label}
      </span>
      <span className="block text-xs text-muted-foreground">
        Invité&nbsp;: {shortDate(row.invitedAt)} · Soumis&nbsp;: {shortDate(row.submittedAt)}
        {row.lastReminderAt !== null
          ? ` · Relancé : ${shortDate(row.lastReminderAt)}`
          : ''}
      </span>
    </span>
  );
}

/**
 * Vue SIREN (DBU l.1141/l.1143) : un agrégat par SIREN, colonne « Complet » (tous les SIRET participants ont
 * soumis). REPLIABLE : clic sur la ligne SIREN développe le détail des SIRET associés (l.1143). Le détail est
 * projeté depuis `rows` (grouped par SIREN côté client) — aucun appel supplémentaire, aucune règle métier.
 * WCAG : chaque ligne SIREN est un <button aria-expanded aria-controls> ; le détail est une région annoncée.
 */
function SupplierMonitoringSirenTable({
  aggregates,
  rows,
  canRelance,
  onRelanceSiren,
}: {
  readonly aggregates: readonly SupplierMonitoringSirenAggregateDto[];
  readonly rows: readonly SupplierMonitoringRowDto[];
  /** Relance disponible (cycle courant connu + pas de relance en cours). */
  readonly canRelance: boolean;
  /** Déclenche une relance en vue SIREN (mode siren : tous les MASTER non complétés du SIREN). */
  readonly onRelanceSiren: (siren: string) => void;
}): React.JSX.Element {
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const rowsBySiren = React.useMemo(() => {
    const map = new Map<string, SupplierMonitoringRowDto[]>();
    for (const r of rows) {
      const list = map.get(r.siren);
      if (list === undefined) {
        map.set(r.siren, [r]);
      } else {
        list.push(r);
      }
    }
    return map;
  }, [rows]);

  const toggle = React.useCallback((siren: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(siren)) {
        next.delete(siren);
      } else {
        next.add(siren);
      }
      return next;
    });
  }, []);

  if (aggregates.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucun SIREN participant dans le périmètre.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Synthèse des fournisseurs par SIREN (nombre de SIRET, soumissions, complétude) — repliable
        </caption>
        <thead>
          <tr className="border-b border-border text-left">
            <th scope="col" className="py-2 pr-4 font-medium">
              SIREN
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Établissements
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Soumissions
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              Complet
            </th>
            <th scope="col" className="py-2 font-medium">
              Relance
            </th>
          </tr>
        </thead>
        <tbody>
          {aggregates.map((agg) => {
            const isExpanded = expanded.has(agg.siren);
            const detailId = `siren-detail-${agg.siren}`;
            const sirenRows = rowsBySiren.get(agg.siren) ?? [];
            return (
              <React.Fragment key={agg.siren}>
                <tr className="border-b border-border last:border-0">
                  <th scope="row" className="py-2 pr-4 font-normal">
                    <button
                      type="button"
                      className="flex items-start gap-2 text-left"
                      aria-expanded={isExpanded}
                      aria-controls={detailId}
                      onClick={() => {
                        toggle(agg.siren);
                      }}
                    >
                      <span aria-hidden="true" className="mt-0.5 select-none text-muted-foreground">
                        {isExpanded ? '▾' : '▸'}
                      </span>
                      <span className="block">
                        <span className="block font-medium">{agg.raisonSociale ?? '—'}</span>
                        <span className="block font-mono text-xs text-muted-foreground">
                          {agg.siren}
                        </span>
                        <span className="sr-only">
                          {isExpanded
                            ? 'Réduire le détail des établissements'
                            : 'Développer le détail des établissements'}
                        </span>
                      </span>
                    </button>
                  </th>
                  <td className="py-2 pr-4 tabular-nums">{String(agg.siretCount)}</td>
                  <td className="py-2 pr-4 tabular-nums">
                    {String(agg.submittedSiretCount)} / {String(agg.siretCount)}
                  </td>
                  <td className="py-2 pr-4">{agg.complet ? 'Complet' : 'Incomplet'}</td>
                  <td className="py-2">
                    {agg.complet ? (
                      <span className="text-xs text-muted-foreground">Complet</span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canRelance}
                        onClick={() => {
                          onRelanceSiren(agg.siren);
                        }}
                        aria-label={`Relancer les établissements non soumis du SIREN ${agg.raisonSociale ?? agg.siren}`}
                      >
                        Relancer
                      </Button>
                    )}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr id={detailId} className="border-b border-border last:border-0">
                    <td colSpan={5} className="bg-muted/30 py-2 pl-6 pr-4">
                      <ul className="space-y-1">
                        {sirenRows.map((r) => (
                          <li
                            key={r.supplierSiret}
                            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5"
                          >
                            <span className="font-mono text-xs">{r.supplierSiret}</span>
                            <span className="text-xs text-muted-foreground">
                              {r.raisonSociale ?? '—'} ·{' '}
                              {participationStateLabel(r.participationState).label} ·{' '}
                              {r.submitted ? 'Soumis' : 'En attente'} ·{' '}
                              {completionLabel(r.completionPct)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Santé OPP-02 : juxtaposition de KPI au vol dans une live-region `aria-live="polite"` — chaque
 * refetch (polling) est annoncé sans voler le focus. Jamais un score unique.
 */
function HealthPanel({
  isPending,
  isError,
  errorMessage,
  health,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly health: CampaignHealthDto | undefined;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Santé de la campagne</CardTitle>
        <CardDescription>
          Indicateurs de participation juxtaposés (jamais agrégés en un score),
          actualisés en continu.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div aria-live="polite" aria-busy={isPending}>
          {isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement de la santé de la campagne…
            </p>
          ) : isError ? (
            <p role="alert" className="text-destructive">
              Erreur de chargement : {errorMessage}
            </p>
          ) : health === undefined ? null : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Kpi
                label="Participation"
                value={`${String(health.participationPct)} %`}
              />
              <Kpi
                label="Fournisseurs ayant soumis"
                value={`${String(health.submittedSupplierCount)} / ${String(health.invitedSupplierCount)}`}
              />
              <Kpi
                label="Fournisseurs invités"
                value={String(health.invitedSupplierCount)}
              />
              <Kpi
                label="Cellules Hors Jeux"
                value={String(health.horsJeuxCount)}
                hint={HORS_JEUX_HINT}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Une alerte de pilotage : sévérité en LIBELLÉ TEXTE (renfort couleur) + message non intrusif. */
function AlertItem({ alert }: { readonly alert: AdminAlertDto }): React.JSX.Element {
  const isCritical = alert.severity === 'critical';
  return (
    <li className="flex items-start gap-3 rounded-md border border-border p-3">
      <Badge
        variant={isCritical ? 'default' : 'secondary'}
        className={isCritical ? 'text-destructive-foreground' : undefined}
      >
        {severityLabel(alert.severity)}
      </Badge>
      <span className="text-sm">{alert.message}</span>
    </li>
  );
}

/**
 * Alertes proactives (§2.5) : in-app, non intrusives (DBU §1.4.9 — aucun push, aucun email admin).
 * Chaque alerte porte un LIBELLÉ TEXTE de sévérité (jamais couleur seule). Live-region polie.
 */
function AlertsPanel({
  isPending,
  isError,
  alerts,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly alerts: readonly AdminAlertDto[] | undefined;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Alertes de pilotage</CardTitle>
        <CardDescription>
          Signalements in-app non intrusifs (échéance proche, participation basse),
          triés par criticité.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div aria-live="polite" aria-busy={isPending}>
          {isPending ? (
            <p role="status" className="text-muted-foreground">
              Chargement des alertes…
            </p>
          ) : isError ? (
            <p role="alert" className="text-destructive">
              Erreur de chargement des alertes.
            </p>
          ) : alerts === undefined || alerts.length === 0 ? (
            <p role="status" className="text-muted-foreground">
              Aucune alerte active.
            </p>
          ) : (
            <ul className="space-y-2">
              {alerts.map((alert, index) => (
                <AlertItem key={`${alert.severity}-${String(index)}`} alert={alert} />
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Progression de réponses par profil (§2.5 Focus Campagne « progression par famille et profil »),
 * alimentée par la matrice Profil × Niveau (taux de réponse au vol). Table WCAG (<caption>/<th scope>).
 */
function ProfileProgressPanel({
  isPending,
  isError,
  errorMessage,
  rows,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly rows: readonly ProfileMatrixRowDto[] | undefined;
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Progression par profil</CardTitle>
        <CardDescription>
          Taux de réponse par couple Profil × Niveau, calculé au vol sur la dernière
          réponse par fournisseur.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement de la progression par profil…
          </p>
        ) : isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement : {errorMessage}
          </p>
        ) : rows === undefined || rows.length === 0 ? (
          <p role="status" className="text-muted-foreground">
            Aucun couple Profil × Niveau dans le périmètre.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full border-collapse text-sm">
              <caption className="sr-only">
                Progression des réponses par couple Profil × Niveau
              </caption>
              <thead>
                <tr className="border-b border-border text-left">
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Profil
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Niveau
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Réponses
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    Taux de réponse
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.profileCode}::${row.seniorityLevel}`}
                    className="border-b border-border last:border-0"
                  >
                    <th scope="row" className="py-2 pr-4 font-normal">
                      {row.profileCode}
                    </th>
                    <td className="py-2 pr-4">
                      {seniorityLabel(row.seniorityLevel)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {String(row.count)}
                    </td>
                    <td className="py-2 tabular-nums">
                      {String(row.responseRatePct)} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
